import { TabularData, TabularDataProps } from "@app-components/TabularData";
import { useRefCallback } from "@enfusion-ui/hooks";
import {
  getDetailRowHeight,
  ReportContextMetaStoreEntry,
  useTheme,
  useThisTab,
  valueTooltipGetter,
} from "@enfusion-ui/web-core";
import {
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
} from "ag-grid-community";
import { noop } from "lodash";
import React from "react";

import {
  ReportContextDataStoreEntry,
  useReports,
} from "../context/reports/context";

export type GridParams = {
  metadata?: ReportContextMetaStoreEntry;
  selectedTableId: string;
  gridOptions: ReportContextDataStoreEntry;
};

const defaultColDef = {
  resizable: true,
  tooltipValueGetter: valueTooltipGetter,
};

export const ReportGrid: React.FC<TabularDataProps & GridOptions> = ({
  colDefs,
  rowData,
  onGridReady,
  onRowGroupOpened,
  masterDetail = false,
  ...props
}) => {
  const { dataStore } = useReports();
  const { selector } = useThisTab();
  const { theme } = useTheme();

  const handleOnGridReadyEvent = useRefCallback(
    (event: GridReadyEvent) => {
      onGridReady?.(event);
    },
    [onGridReady]
  );

  const popupParent = React.useMemo(
    () => document.getElementById(selector) ?? undefined,
    [selector]
  );

  const args = React.useMemo(
    () =>
      ({
        dataStore,
        suppressCellFocus: true,
        detailRowAutoHeight: true,
        suppressColumnMoveAnimation: true,
        domLayout: "normal",
        multiSortKey: "ctrl",
        getDetailRowHeight,
        onGridReady,
        defaultColDef,
        getRowId: (row) => row.data.__row_id,
        enableCellChangeFlash: theme.cellFlashing,
        popupParent,
        onRowGroupOpened,
      } as GridOptions),
    [theme, popupParent, dataStore]
  );

  const detailCellRendererParams: any = useRefCallback(
    (params: ICellRendererParams) => {
      return {
        detailGridOptions: {
          ...props,
          ...args,
          context: {
            isChildGrid: true,
            tableId: params.data.__row_id,
            metadata: dataStore[params.data.__row_id]?.metadata,
            rows: dataStore[params.data.__row_id]?.rows,
          },
          masterDetail,

          detailCellRendererParams,
        } as GridOptions,
        getDetailRowData: noop,
      };
    },
    [dataStore, props, args, dataStore, masterDetail]
  );
  return (
    <>
      <TabularData
        {...props}
        colDefs={colDefs}
        rowData={rowData}
        onGridReady={handleOnGridReadyEvent}
        onRowGroupOpened={onRowGroupOpened}
        detailCellRendererParams={detailCellRendererParams}
        masterDetail={masterDetail}
        detailRowAutoHeight
      />
    </>
  );
};
